import { Button, Card, Col, Row, Typography } from "antd";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  dashboardFilterOptions,
  dashboardTableDesktopHeaders,
} from "../../helpers/dashboardConstants";
import useMultiNotification from "../../hooks/useNotification";
import { getMiniFormulasApi } from "../../redux-toolkit/slices/formulasSlice";
import { getAllOrdersApi } from "../../redux-toolkit/slices/ordersSlice";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import AppPagination from "../Items/AppPagination";
import AppRangePicker from "../Items/AppRangePicker";
import AppSelectFilter from "../Items/AppSelectFilter";
import DashboardItemCard from "../Items/cards/DashboardItemCard";
import SearchComponent from "../Items/Search";
import TableHeader from "../Items/Table/Header";
import LeftCol from "../wrappers/NewEntryFormLeftColWrapper";
import RightCol from "../wrappers/NewEntryFormRightColWrapper";
import DashboardTableRow from "./DashboardTableRow";
import AppLoader from "../Items/loader/AppLoader";

const AdminDashboard = () => {
  const { contextHolder, openNotification } = useMultiNotification();
  const generalState = useSelector((state: RootState) => state.general);
  const { ordersData, reloadApiCount, isOrdersApiLoading } = useSelector(
    (state: RootState) => state.orders
  );
  const { isShowTableInDesktop } = generalState;
  const dispatch = useAppDispatch();
  const pagination = {
    currentPage: ordersData.currentPage,
    totalPages: ordersData.totalPages,
    nextPage: ordersData.nextPage,
    previousPage: ordersData.previousPage,
    totalCount: ordersData.totalCount,
    pageSize: ordersData.pageSize,
  };

  // state
  const [activePage, setActivePage] = useState<number>(1);

  const [dashboardHeader, setDashboardHeaders] = useState<
    DesktopTableHeaderOption[]
  >(dashboardTableDesktopHeaders);

  //state end
  const handleOnSearch = (search: string) => {};
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [filters, setFilters] = useState<OrdersFilter>({
    orderStatus: undefined,
    state: undefined,
    isFirstOrder: undefined,
    startDate: undefined,
    endDate: undefined,
  });
  useEffect(() => {
    dispatch(getMiniFormulasApi(activePage));
  }, []);
  useEffect(() => {}, []);

  const handleSelectChange = (value: string, filterKey: string) => {
    setSelectedValue(value);
    setFilters({
      ...filters,
      [filterKey]: value,
    });
  };

  const handleRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null,
    dateStrings: [string, string] | null
  ) => {
    if (!dates) {
      setFilters({
        ...filters,
        startDate: undefined,
        endDate: undefined,
      });
      return;
    }
    const [startDate, endDate] = dates;
    setFilters({
      ...filters,
      startDate: startDate?.format("YYYY-MM-DD"),
      endDate: endDate?.format("YYYY-MM-DD"),
    });
  };
  const handleCheckBox = (checked: boolean, id: string) => {};
  useEffect(() => {
    handleFetchOrders(activePage, filters);
  }, [filters]);
  const handleFetchOrders = (activePage: number, filters: OrdersFilter) => {
    let query = `?page=${activePage}`;
    if (filters.isFirstOrder) {
      query += `&isFirstOrder=${filters.isFirstOrder}`;
    }
    if (filters.orderStatus) {
      query += `&orderStatus=${filters.orderStatus}`;
    }
    if (filters.state) {
      query += `&selectedState=${filters.state}`;
    }
    if (filters.startDate && filters.endDate) {
      query += `&startDate=${filters.startDate}&endDate=${filters.endDate}`;
    }
    dispatch(getAllOrdersApi(query));
  };

  useEffect(() => {
    if (reloadApiCount > 0) {
      handleFetchOrders(pagination.currentPage, filters);
    }
  }, [reloadApiCount]);
  if (isOrdersApiLoading) {
    return <AppLoader />;
  }
  return (
    <>
      {contextHolder}
      <Card>
        <Row gutter={[16, 16]}>
          <LeftCol>
            <Typography className="dashboard-page-title">
              Orders List
            </Typography>
          </LeftCol>
          <RightCol>
            <div className="dashboard-search-container">
              {/* <SearchComponent width="50%" onSearch={handleOnSearch} /> */}
            </div>
          </RightCol>
        </Row>
        <Row className="mt-10 mb-10" gutter={[16, 16]}>
          <Col span={24}>
            <Typography className="dashboard-page-sub-title">
              Filters
            </Typography>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col md={6} lg={6} sm={12} xs={12}>
            <AppSelectFilter
              label={dashboardFilterOptions.STATUS.LABEL}
              options={dashboardFilterOptions.STATUS.options}
              onChange={(value) => handleSelectChange(value, "orderStatus")}
              placeHolder={dashboardFilterOptions.STATUS.placeHolder}
            />
          </Col>
          <Col md={6} lg={6} sm={12} xs={12}>
            <AppRangePicker
              placeHolder=""
              onChange={handleRangeChange}
              label="Order date range"
            />
          </Col>
          <Col md={6} lg={4} sm={12} xs={12}>
            <AppSelectFilter
              label={dashboardFilterOptions.FIRST_TIME_USER.LABEL}
              options={dashboardFilterOptions.FIRST_TIME_USER.options}
              onChange={(value) => handleSelectChange(value, "isFirstOrder")}
              placeHolder={dashboardFilterOptions.FIRST_TIME_USER.placeHolder}
            />
          </Col>
          <Col md={6} lg={4} sm={12} xs={12}>
            <AppSelectFilter
              label={dashboardFilterOptions.STATE.LABEL}
              options={dashboardFilterOptions.STATE.options}
              onChange={(value) => handleSelectChange(value, "state")}
              placeHolder={dashboardFilterOptions.STATE.placeHolder}
            />
          </Col>
        </Row>
        <Row>
          <Col
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "10px",
              marginTop: "10px",
            }}
            span={24}
          >
            <Button type="primary">Bulk Action</Button>
          </Col>
        </Row>
        {isShowTableInDesktop === true ? (
          <>
            <Row className="table-container">
              <TableHeader
                headOptions={dashboardHeader}
                handleCheckBox={handleCheckBox}
              />
              {ordersData.entries.map((order, index) => {
                return (
                  <DashboardTableRow
                    key={`orders-desktop-row-${index}`}
                    order={order}
                    activeBg={index % 2 === 0 ? "#F9F9F9" : "#fff"}
                  />
                );
              })}
            </Row>
          </>
        ) : (
          <>
            <Row gutter={[16, 16]}>
              {ordersData.entries.map((order, index) => {
                return (
                  <Col
                    key={`orders-table-mobile-index-${index}`}
                    lg={12}
                    md={12}
                    xl={12}
                    xxl={12}
                    sm={24}
                    xs={24}
                  >
                    <DashboardItemCard
                      order={order}
                      key={`orders-table-mobile-card-${index}`}
                    />
                  </Col>
                );
              })}
            </Row>
          </>
        )}
        <Row className="pagination-outer-wrapper">
          <AppPagination
            pagination={pagination}
            handlePageSwitch={(page) => {
              handleFetchOrders(page, filters);
            }}
          />
        </Row>
      </Card>
    </>
  );
};
export default AdminDashboard;
